import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Zap, TrendingUp, Globe2, Plus, FileText, ArrowRight, BarChart3, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Fliboe Pulse — landing for visitors, dashboard for logged-in users. */
export default function Pulse() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [buyingPkg, setBuyingPkg] = useState("");

  const PULSE_TIERS = [
    {
      id: "pulse_starter_1mo",
      name: "Starter",
      price: "$19",
      cadence: "/month",
      posts: "4 posts",
      sites: "1 site",
      highlights: [
        "AI-written long-form posts",
        "Nano Banana cover images",
        "Hosted /p/your-slug pages",
        "Single-site embed widget",
      ],
      cta: "Start Pulse Starter",
      featured: false,
    },
    {
      id: "pulse_pro_1mo",
      name: "Pro",
      price: "$49",
      cadence: "/month",
      posts: "20 posts",
      sites: "3 sites",
      highlights: [
        "Everything in Starter",
        "3 separate Pulse sites",
        "Priority queue (faster generation)",
        "Email support",
      ],
      cta: "Go Pro",
      featured: true,
    },
    {
      id: "pulse_agency_1mo",
      name: "Agency",
      price: "$149",
      cadence: "/month",
      posts: "Unlimited posts",
      sites: "10 sites",
      highlights: [
        "Everything in Pro",
        "Up to 10 Pulse sites",
        "White-label embed (no Fliboe footer)",
        "Direct Slack support",
      ],
      cta: "Go Agency",
      featured: false,
    },
    {
      id: "pulse_earn_1mo",
      name: "Earn",
      price: "$49.99",
      cadence: "/month",
      posts: "30 posts",
      sites: "3 sites",
      highlights: [
        "Everything in Pro, plus:",
        "Ezoic ad units auto-injected in your posts",
        "Quarterly ad-revenue share payouts",
        "Turns your Pulse blog into a revenue stream",
      ],
      cta: "Start earning",
      featured: false,
      hidden: true, // Toggle to false when Ezoic is approved and we're ready to go live.
    },
  ];

  const subscribe = async (pkgId) => {
    if (!user) { navigate("/login?next=/pulse"); return; }
    setBuyingPkg(pkgId);
    try {
      const { data } = await api.post(
        "/payments/checkout",
        { package_id: pkgId, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${localStorage.getItem("fliboe_token")}` } },
      );
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start checkout. Try again in a moment.");
      setBuyingPkg("");
    }
  };

  // Pricing tier section — rendered in BOTH the logged-in dashboard AND the
  // public landing so paying customers always see plans without bouncing.
  const renderTiers = () => (
    <section id="pricing-tiers" className="max-w-6xl mx-auto px-5 py-16 scroll-mt-24" data-testid="pulse-tiers">
      <div className="text-center mb-10">
        <div className="fl-mono text-xs uppercase tracking-[0.3em] text-fuchsia-400 mb-3">// Pulse plans</div>
        <h2 className="fl-display text-3xl sm:text-4xl font-bold text-white mb-3">
          Pick your tier. Cancel anytime.
        </h2>
        <p className="text-white/55 max-w-xl mx-auto">
          For your own business or personal site only. Resale, sub-licensing, or
          offering Pulse output to the public as a service is not permitted.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PULSE_TIERS.filter(t => !t.hidden).map(tier => (
          <div
            key={tier.id}
            className={`fl-card p-6 flex flex-col ${tier.featured ? "border-fuchsia-400/60 shadow-[0_0_40px_-12px_rgba(232,121,249,0.45)]" : ""}`}
            data-testid={`pulse-tier-${tier.id}`}
          >
            {tier.featured && (
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-fuchsia-300 mb-2">Most popular</div>
            )}
            <div className="fl-display text-2xl font-bold text-white">{tier.name}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="fl-display text-5xl font-black text-white">{tier.price}</span>
              <span className="text-white/40 text-sm">{tier.cadence}</span>
            </div>
            <div className="mt-2 text-white/70 text-sm font-semibold">{tier.posts} · {tier.sites}</div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {tier.highlights.map(h => (
                <li key={h} className="flex items-start gap-2 text-sm text-white/75">
                  <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.featured ? "text-fuchsia-400" : "text-emerald-400"}`} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => subscribe(tier.id)}
              disabled={buyingPkg === tier.id}
              className={`fl-btn mt-6 ${tier.featured ? "" : "ghost"}`}
              data-testid={`pulse-tier-cta-${tier.id}`}
            >
              {buyingPkg === tier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {buyingPkg === tier.id ? "Redirecting…" : tier.cta}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center text-xs text-white/35">
        Free accounts get 1 post/month to taste it. Existing Fliboe broadcasters get 4–8 posts/mo as a perk.
      </div>
    </section>
  );

  useEffect(() => {
    document.title = "Fliboe Pulse — AI SEO blog autopilot";
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setLoadingSites(true);
    (async () => {
      try {
        const { data } = await api.get("/pulse/sites", { headers: authHeaders() });
        if (alive) { setSites(data.sites || []); setQuota(data.quota || null); }
      } catch (_) { /* noop */ }
      finally { if (alive) setLoadingSites(false); }
    })();
    return () => { alive = false; };
  }, [user]);

  if (loading) return null;

  // ---------- LOGGED-IN DASHBOARD ----------
  if (user) {
    return (
      <main className="max-w-6xl mx-auto px-5 py-12" data-testid="pulse-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5" /> Fliboe Pulse
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-white">
              Your AI content engine.
            </h1>
            <p className="text-white/60 mt-2">Generate SEO articles for <strong className="text-white/80">your own business or personal site</strong> — built to rank on Google, Bing and AI answer engines (ChatGPT, Perplexity).</p>
          </div>
          {quota && (
            <div className="text-right" data-testid="pulse-quota">
              <div className="text-xs uppercase tracking-widest text-white/40">This month</div>
              <div className="text-2xl font-extrabold text-white">{quota.used_this_month}<span className="text-white/30 text-base"> / {quota.monthly_cap}</span></div>
              <div className="text-xs text-white/50">{quota.remaining} posts remaining</div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => navigate("/pulse/new")}
            className="fl-btn"
            data-testid="pulse-new-site-btn"
          >
            <Plus className="w-4 h-4" /> New site
          </button>
          <Link to="/pricing" className="fl-btn ghost" data-testid="pulse-upgrade-btn">
            <Zap className="w-4 h-4 text-emerald-400" /> Upgrade for more posts
          </Link>
        </div>

        {loadingSites ? (
          <div className="text-white/40 text-sm">Loading sites…</div>
        ) : sites.length === 0 ? (
          <div className="fl-card p-10 text-center" data-testid="pulse-empty-state">
            <Globe2 className="w-12 h-12 text-fuchsia-400 mx-auto mb-3" />
            <h2 className="fl-display text-2xl font-bold mb-2">Spin up your first Pulse site</h2>
            <p className="text-white/60 max-w-md mx-auto mb-5">
              A Pulse site is one topical blog — like &quot;Internet Radio for Indie Artists&quot;.
              You add a few keywords, Pulse writes posts and paints covers on demand.
            </p>
            <button onClick={() => navigate("/pulse/new")} className="fl-btn" data-testid="pulse-empty-cta">
              <Plus className="w-4 h-4" /> Create your first site
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pulse-sites-grid">
            {sites.map(s => (
              <Link
                to={`/pulse/${s.site_id}`}
                key={s.site_id}
                className="fl-card p-5 hover:border-fuchsia-400/40 transition-colors group"
                data-testid={`pulse-site-card-${s.site_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-fuchsia-300/80 uppercase tracking-widest font-semibold">{s.niche}</div>
                    <div className="text-white font-bold text-lg mt-1 truncate">{s.name}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/80 transition-colors flex-shrink-0 mt-1" />
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(s.keywords || []).slice(0, 4).map(k => (
                    <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{k}</span>
                  ))}
                  {(s.keywords || []).length > 4 && (
                    <span className="text-xs px-2 py-0.5 text-white/40">+{s.keywords.length - 4}</span>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-4 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {s.published_count || 0} published</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Plans — also shown to logged-in users so they can upgrade without leaving */}
        {renderTiers()}
      </main>
    );
  }

  // ---------- PUBLIC LANDING ----------
  return (
    <main data-testid="pulse-landing">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-12 sm:pt-24">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/60 via-black/75 to-[#050505]" />
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at top left, rgba(16,185,129,0.18), transparent 55%), radial-gradient(ellipse at bottom right, rgba(217,70,239,0.12), transparent 55%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-xs font-semibold tracking-wide uppercase mb-6">
          <Sparkles className="w-3.5 h-3.5" /> New — Fliboe Pulse
        </div>
        <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-white leading-[0.95]">
          Rank higher on Google,<br />
          Bing &amp; <span className="text-fuchsia-400">ChatGPT.</span>
        </h1>
        <p className="text-white/65 text-lg sm:text-xl mt-6 max-w-2xl">
          Pulse writes long-form SEO articles for <strong className="text-white">your own business or personal site</strong> — Claude does the writing, Nano Banana paints cover art. Designed to win the new wave of search: classic Google + Bing <em>and</em> AI answer engines like ChatGPT and Perplexity.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/signup" className="fl-btn" data-testid="pulse-landing-cta-signup">
            <Sparkles className="w-4 h-4" /> Start your first post free
          </Link>
          <a href="#pricing-tiers" className="fl-btn ghost" data-testid="pulse-landing-cta-pricing">
            See tiers <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <div className="mt-6 text-xs text-white/40 fl-mono uppercase tracking-widest">
          // For your own site only · No reselling · 1 free post on every account
        </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-6xl mx-auto px-5 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Zap, title: "Claude-grade writing", body: "Long-form, original takes. Modern Google helpful-content guidelines. No AI fluff, no boilerplate." },
            { icon: Sparkles, title: "Nano Banana covers", body: "Every post ships with a custom 16:9 cinematic hero image — generated, not stock." },
            { icon: Globe2, title: "Embed on YOUR site", body: "Drop a single <script> on your own business or personal site. Pulse mounts your blog. Send traffic to your hosted /p/your-slug pages." },
            { icon: TrendingUp, title: "Built for AI search", body: "Optimized for Google + Bing crawlers AND for LLM answer engines — ChatGPT, Perplexity, Claude search — which already drive a fast-growing share of traffic." },
            { icon: BarChart3, title: "Live on your dashboard", body: "Quota meter. Post list. One-click generate. Delete, re-roll, and ship without friction." },
            { icon: FileText, title: "SEO meta + sitemap-ready", body: "Title, meta description, slug, Open Graph — all dialed in per post for max indexability." },
          ].map((f, i) => (
            <div key={i} className="fl-card p-5">
              <f.icon className="w-5 h-5 text-fuchsia-400 mb-3" />
              <div className="text-white font-bold mb-1">{f.title}</div>
              <div className="text-white/55 text-sm leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing tiers */}
      {renderTiers()}

      {/* Pricing teaser */}
      <section className="max-w-4xl mx-auto px-5 py-16 text-center">
        <div className="fl-mono text-xs uppercase tracking-[0.3em] text-fuchsia-400 mb-3">// Plain English</div>
        <h2 className="fl-display text-3xl sm:text-4xl font-bold text-white mb-3">
          Built for your business. Not for resale.
        </h2>
        <p className="text-white/60 max-w-xl mx-auto">
          Pulse is licensed for use on your own business or personal site only.
        </p>
        <Link to="/signup" className="fl-btn mt-8 inline-flex" data-testid="pulse-landing-cta-bottom">
          <Sparkles className="w-4 h-4" /> Claim your free post
        </Link>
        <div className="mt-6 text-xs text-white/40 max-w-xl mx-auto">
          By using Pulse you agree to our usage terms: posts may be published only on websites
          you own or operate. Reselling, sub-licensing, or offering Pulse output to the public
          as a service is not permitted.
        </div>
      </section>
    </main>
  );
}
